import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  useApproveMemberMutation,
  useBulkApproveMembersMutation,
  useDeclineMemberMutation,
  useSuspendMemberMutation,
  useDeleteMemberMutation,
  useArchiveMemberMutation,
  useUnSuspendMemberMutation,
  useUnarchiveMemberMutation,
  useActivateMemberMutation,
  useUpdateMemberMutation,
  useChangeMemberBranchMutation,
} from "../../../store/admin/memberManagement/memberSlice";
const useMemberRegAction = () => {
  const navigate = useNavigate();
  const [declineMember] = useDeclineMemberMutation();
  const [approveMember] = useApproveMemberMutation();
  const [bulkApproveMembers] = useBulkApproveMembersMutation();
  const [suspend_member] = useSuspendMemberMutation();
  const [delete_member] = useDeleteMemberMutation();
  const [archive_member] = useArchiveMemberMutation();
  const [un_suspend_member] = useUnSuspendMemberMutation();
  const [unarchive_member] = useUnarchiveMemberMutation();
  const [activate_member] = useActivateMemberMutation();
  const [editMember] = useUpdateMemberMutation();
  const [changeBranch, { isLoading: isChangingBranch }] =
    useChangeMemberBranchMutation();

  const toastNotify = (error, data, toastId) => {
    if (error && error.status === 401) {
      console.log(error);
      toast.error("Login to continue", {
        id: toastId,
      });
      return navigate("/login");
    }
    if (error) {
      return toast.error(error?.data.message || "An error occured", {
        id: toastId,
      });
    }
    if (data.status === false) {
      return toast.error(data.message || "An error occured", { id: toastId });
    }
    return toast.success(data.message || "success", { id: toastId });
  };

  const rejectMember = async (memberId) => {
    const toastId = toast.loading("Declining");
    const { data, error } = await declineMember(memberId);
    toastNotify(error, data, toastId);
  };

  const acceptMember = async (memberId) => {
    const toastId = toast.loading("Approving");
    const { data, error } = await approveMember(memberId);
    toastNotify(error, data, toastId);
  };

  const acceptMembersBulk = async (memberIds) => {
    if (!Array.isArray(memberIds) || memberIds.length === 0) {
      return { ok: false };
    }
    const toastId = toast.loading(
      `Approving ${memberIds.length} member${memberIds.length === 1 ? "" : "s"}…`
    );
    const { data, error } = await bulkApproveMembers(memberIds);
    if (error && error.status === 401) {
      toast.error("Login to continue", { id: toastId });
      navigate("/login");
      return { ok: false };
    }
    if (error) {
      toast.error(error?.data?.message || "Bulk approval failed", {
        id: toastId,
      });
      return { ok: false };
    }
    if (data?.status === false) {
      toast.error(data?.message || "Bulk approval failed", { id: toastId });
      return { ok: false };
    }

    const summary = data?.data || {};
    const approvedCount = summary.approved_count ?? 0;
    const failedCount = summary.failed_count ?? 0;
    const firstFailReason = summary.failed?.[0]?.reason;

    if (failedCount > 0 && approvedCount === 0) {
      toast.error(
        firstFailReason
          ? `None approved. ${firstFailReason}`
          : data?.message || "None of the selected members could be approved",
        { id: toastId }
      );
      return { ok: false, summary };
    }

    if (failedCount > 0) {
      toast.success(
        `${data?.message || `Approved ${approvedCount}`}${
          firstFailReason ? `. First failure: ${firstFailReason}` : ""
        }`,
        { id: toastId }
      );
    } else {
      toast.success(data?.message || `Approved ${approvedCount} member(s)`, {
        id: toastId,
      });
    }
    return { ok: true, summary };
  };

  const suspendMember = async (body) => {
    const toastId = toast.loading("Suspending member");
    const { data, error } = await suspend_member(body);
    toastNotify(error, data, toastId);
  };

  const unSuspendMember = async (memberId) => {
    const toastId = toast.loading("Un Suspending member");
    const { data, error } = await un_suspend_member(memberId);
    toastNotify(error, data, toastId);
  };

  const deleteMember = async (memberId) => {
    const toastId = toast.loading("Removing member");
    const { data, error } = await delete_member(memberId);
    toastNotify(error, data, toastId);
  };

  const archiveMember = async (memberId) => {
    const toastId = toast.loading("Archiving member");
    const { data, error } = await archive_member(memberId);
    toastNotify(error, data, toastId);
  };

  const unarchiveMember = async (memberId) => {
    const toastId = toast.loading("Unarchiving member");
    const { data, error } = await unarchive_member(memberId);
    toastNotify(error, data, toastId);
  };

  const activateMember = async (memberId) => {
    const toastId = toast.loading("Activating member");
    const { data, error } = await activate_member(memberId);
    toastNotify(error, data, toastId);
  };

  const updateMember = async (body) => {
    const toastId = toast.loading("Updating member");
    const { data, error } = await editMember(body);
    toastNotify(error, data, toastId);
  };

  const changeMemberBranch = async (memberId, reqBody) => {
    const toastId = toast.loading("changing member branch");
    const { data, error } = await changeBranch({ memberId, reqBody });
    toastNotify(error, data, toastId);
  };
  return {
    rejectMember,
    acceptMember,
    acceptMembersBulk,
    suspendMember,
    deleteMember,
    archiveMember,
    unSuspendMember,
    unarchiveMember,
    activateMember,
    updateMember,
    changeMemberBranch,
    isChangingBranch,
  };
};

export default useMemberRegAction;
